import React, { useMemo, useContext, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import Item from "../Item";
import EditableElement from "../../../editor/components/EditableElement";
import { EditContext } from "../../../editor/context/EditContext";
import { useAddElement } from "../../../editor/context/AddElementContext";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DragHandleProvider } from "../../../editor/context/DragHandleContext";
import { makeSortableId } from "../../../editor/utils/reorderCache";
import AddButtonWithIndicator from "../../../editor/components/AddButtonWithIndicator";
import AddButtonOverlay from "../../../editor/add-buttons/AddButtonOverlay";
import { useDroppable } from "@dnd-kit/core";
import { apiFetch } from "../../../core/utils/api";
import { Z_INDEX } from "../../../core/utils/zIndexScale";
import { useBackdropTextColor } from "../../../core/utils/backdropContrast";
import "./componentEditAffordances.css";

const hasFullData = (child) => {
  if (!child) return false;
  const minimalFields = ["id", "type", "order"];
  return Object.keys(child).some((f) => !minimalFields.includes(f));
};

/**
 * Decide whether an image/video element should be treated as having renderable
 * media (real file) versus being an empty "No Image Selected" placeholder.
 *
 * In the editor (normal mode) elements arrive as minimal refs ({id, type, order})
 * without `image_url`/`video_url`; the actual media is fetched lazily by `Item`.
 * In that case we cannot tell whether the element is a placeholder, so we assume
 * it has media — otherwise full-bleed cover layouts would always fall back to the
 * contained layout. We only treat an element as a placeholder when we have its
 * full data AND its media URLs are empty.
 */
export const elementHasRenderableMedia = (element) => {
  if (!element) return false;
  if (!hasFullData(element)) return true; // minimal ref → media unknown, assume real
  return !!(element.image_url || element.video_url);
};

// Tailwind default border-radius values keyed by their `rounded-*` class. Used to
// translate the image's configured rounding into a real CSS length for `clip-path`
// (which cannot take a class). Mirrors the values in `tailwind.config.js`.
const TILE_RADIUS_BY_CLASS = {
  "rounded-none": "0px",
  "rounded-sm": "0.125rem",
  rounded: "0.25rem",
  "rounded-md": "0.375rem",
  "rounded-lg": "0.5rem",
  "rounded-xl": "0.75rem",
  "rounded-2xl": "1rem",
  "rounded-3xl": "1.5rem",
  "rounded-full": "9999px",
};

const DEFAULT_TILE_ROUNDED = "rounded-lg";

// Utility classes that strip the inner ProgressiveImage wrapper's hardcoded 8px
// rounding (it sets `borderRadius: 8px; overflow: hidden` inline). In a cover
// "frame" tile we want the TILE to be the single source of rounding so the
// configured radius actually shows — otherwise the wrapper re-clips every cover
// image to 8px regardless of the image's `rounded_border`.
export const COVER_WRAPPER_UNROUND =
  "[&_.progressive-image-wrapper]:!rounded-none [&_img]:!rounded-none";

// Like COVER_WRAPPER_UNROUND, but ONLY neutralizes the ProgressiveImage
// wrapper's hardcoded inline 8px rounding — it leaves the <img>'s OWN configured
// rounding (`container_style.rounded_border`, applied by `Image.js`) intact.
//
// Use this on STATIC cover tiles (no transform) so the visible corner radius
// equals the image's own radius in BOTH preview and the editor. The renderer
// itself can't know the radius while editing (child elements arrive as minimal
// {id,type,order} refs without `container_style`), but `Image.js` renders the
// real `<img>` with the correct `rounded_border` once its data is fetched — so
// letting the image round itself is the single, always-correct source of
// rounding. The tile stays square `overflow-hidden`; since the cover `<img>`
// fills it exactly, the image's own rounded corners are what shows (a square
// image → square tile; a `rounded-2xl` image → `rounded-2xl` tile).
//
// (Animated "drift" tiles still need COVER_WRAPPER_UNROUND + a `clip-path`
// frame, because their image is scaled and a border-radius on the scaling layer
// would distort — they cannot use this.)
export const COVER_WRAPPER_UNROUND_KEEP_IMG =
  "[&_.progressive-image-wrapper]:!rounded-none";

// In an automatic layout the LAYOUT owns the spacing between/around media — each
// image's own per-side MARGIN must be forced to 0, otherwise it pushes the image
// off-center inside (or out of) its tile (e.g. a `bottom-right` margin parked the
// image in a corner). PADDING stays image-defined (untouched). Margin classes
// from `useSpacing` are plain (non-`!important`) per-side utilities (`mt-4`,
// `ml-2`, `m-auto`, …) on the `<img>`/`<video>`, so a single `!m-0` shorthand
// override wins. Applied at the automatic dispatcher root so it cascades to every
// media element rendered by any mode.
//
// The empty-image "No Image Selected" placeholder (`Image.js`) is a `<div>`, not
// an `<img>`, so the media selectors above never reach it — its own per-side
// margin (newly created images default to margin 1) then pushed it into the cover
// cell's `overflow-hidden` clip and it rendered cut. `.image-empty-placeholder`
// zeroes that margin too so the placeholder fills its cell uncut. (Padding stays.)
export const AUTO_LAYOUT_MEDIA_NO_MARGIN =
  "[&_img]:!m-0 [&_video]:!m-0 [&_.image-empty-placeholder]:!m-0";

/**
 * Resolve the corner rounding for a cover "frame" tile from the image element's
 * configured rounding.
 *
 * The radius is defined on the backend by `container_style.rounded_border`
 * (see `website/models/core/items/image.py` → ContainerStyle), a Tailwind
 * `rounded-*` class such as `rounded-none`, `rounded-lg`, `rounded-2xl`, or
 * `rounded-full`. The frame area must round to exactly this value so its border
 * matches the image's everywhere — including while a Ken Burns "drift" tile
 * zooms (which needs a `clip-path` inset, since `overflow-hidden + border-radius`
 * is not reliably applied to a transformed/composited child).
 *
 * Falls back to `rounded-lg` only when the rounding is unknown — e.g. the editor
 * passes minimal element refs ({id,type,order}) without `container_style`, so we
 * keep the historical default while editing.
 *
 * @param {Object} element - image element (may be a minimal ref in edit mode)
 * @returns {{className: string, radius: string, clip: string}}
 */
export function coverFrameRounding(element) {
  let className = DEFAULT_TILE_ROUNDED;
  const cs = element?.container_style;
  if (cs && Object.prototype.hasOwnProperty.call(cs, "rounded_border")) {
    const raw = cs.rounded_border;
    if (!raw || raw === "none") {
      className = "rounded-none";
    } else if (Object.prototype.hasOwnProperty.call(TILE_RADIUS_BY_CLASS, raw)) {
      className = raw;
    }
  }
  const radius = TILE_RADIUS_BY_CLASS[className] || "0.5rem";
  return { className, radius, clip: `inset(0 round ${radius})` };
}

export function sortChildElements(elements = []) {
  return (elements || [])
    .filter(Boolean)
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
}

export function bucketElements(elements, modeDefinition) {
  const reorderBuckets = modeDefinition?.reorder_buckets || {};
  const typeToBucket = new Map();
  Object.entries(reorderBuckets).forEach(([bucket, types]) => {
    (types || []).forEach((type) => typeToBucket.set(type, bucket));
  });

  const bucketed = {};
  Object.keys(reorderBuckets).forEach((bucket) => {
    bucketed[bucket] = [];
  });
  const unassigned = [];

  sortChildElements(elements).forEach((el) => {
    const bucket = typeToBucket.get(el?.type);
    if (bucket && bucketed[bucket]) {
      bucketed[bucket].push(el);
    } else if (el?.type !== "component") {
      unassigned.push(el);
    }
  });

  return { bucketed, unassigned, typeToBucket };
}

/**
 * SortableItemWrapper — a draggable/sortable node for a single child item.
 *
 * `className`/`style` are applied to the sortable DOM node itself so callers
 * can let the sortable node BE the layout element (e.g. a CSS-grid cell or a
 * cover "frame" tile) instead of nesting an extra wrapper inside it. dnd-kit's
 * drag transform/transition/opacity are merged on top of any caller `style`
 * (so the caller's static styles — clip-path, min-height — are preserved while
 * the drag transform takes over only during a drag).
 *
 * `dragAnywhereEnabled: true` makes the whole tile the drag affordance (no
 * separate handle pill), matching how every other automatic-mode item drags.
 */
export const SortableItemWrapper = React.memo(
  ({ id, data, children, className = "", style: styleProp }) => {
    const stableData = useMemo(() => data, [JSON.stringify(data ?? null)]);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
      useSortable({ id, data: stableData });
    const style = {
      ...(styleProp || {}),
      transform: transform
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : styleProp?.transform,
      transition,
      opacity: isDragging ? 0.18 : (styleProp?.opacity ?? 1),
    };
    return (
      <div ref={setNodeRef} style={style} className={className || undefined} {...attributes}>
        <DragHandleProvider
          value={{ dragListeners: listeners, dragAnywhereEnabled: true }}
        >
          {children}
        </DragHandleProvider>
      </div>
    );
  },
);
SortableItemWrapper.displayName = "SortableItemWrapper";

export const RenderItem = ({ element }) => {
  if (!element?.type) return null;
  return (
    <Item
      {...(hasFullData(element)
        ? { item: element }
        : { itemRef: element })}
    />
  );
};

// Media element types (the "image" reorder bucket accepts both). The
// inter-item "+" add affordance is intentionally NOT rendered next to media
// tiles — it clutters image collages/grids — while titles, text and buttons
// keep their leading-edge add button. (Adding an image is still possible via
// the trailing bucket "add at end" button and the full picker it opens.)
const MEDIA_ITEM_TYPES = new Set(["image", "video"]);
export const isMediaElement = (el) => MEDIA_ITEM_TYPES.has(el?.type);

/**
 * InterItemAddButton — a small "+" affordance pinned to the LEADING edge of its
 * (relatively-positioned) parent cell. Clicking opens the FULL `AddElementPanel`
 * (no type restriction) at `order`, so the editor can insert ANY element type at
 * that position — and the backend reindex (`_reindex_children_after_insert`)
 * shifts later siblings forward, so an inserted Image lands exactly where the
 * button sits (between the two elements it separates).
 *
 * It is positioned fully INSIDE the cell bounds (a thin leading strip, not a
 * straddling/translated overlay) so an `overflow-hidden` cover tile can never
 * clip it. Only the button itself is interactive (`pointer-events`), so the
 * surrounding strip never swallows clicks meant for the underlying item.
 *
 * `orientation`:
 *   - "horizontal" → strip across the TOP edge (use for vertical/stacked lists)
 *   - "vertical"   → strip down the LEFT edge (use for horizontal/row layouts)
 */
export const InterItemAddButton = ({
  compId,
  order,
  orientation = "horizontal",
  className = "",
}) => {
  const editContext = useContext(EditContext);
  const { openAddModal } = useAddElement();
  if (!editContext) return null;

  const isVertical = orientation === "vertical";
  const stripClasses = isVertical
    ? "absolute top-0 bottom-0 left-0 w-7"
    : "absolute left-0 right-0 top-0 h-7";

  return (
    <div
      className={`${stripClasses} pointer-events-none ${className}`.trim()}
      style={{ zIndex: Z_INDEX.ADD_BUTTON_BASE }}
      data-inter-item-add="true"
    >
      <AddButtonWithIndicator
        small
        orientation={orientation}
        variant="component"
        layerToken={isVertical ? "component-row-before" : "component-linear-before"}
        ariaLabel="Add element here"
        onClick={(e) => {
          e.stopPropagation();
          openAddModal({ componentId: compId, order }, e);
        }}
      />
    </div>
  );
};

/**
 * Renders a bucket of items (media, titles, texts, buttons) in a row.
 * Each mode renderer uses this to render its logical slots.
 *
 * In edit mode an `InterItemAddButton` is pinned to the leading edge of every
 * NON-MEDIA cell so the editor can insert a new element BEFORE that item (the
 * trailing "add at end" affordance is still rendered separately by each mode
 * via `BucketAddButton`). Media items (image/video) intentionally do NOT get
 * the inter-item "+" — it clutters image grids/collages; images are still
 * added via the trailing bucket button. `addOrientation` controls where the
 * inter-item button sits; when omitted it is inferred from `className` (a
 * flex/row layout gets a left-edge "vertical" button, everything else a
 * top-edge "horizontal" one).
 */
export const BucketRow = ({
  elements,
  compId,
  className = "",
  style,
  cellClassName = "",
  cellStyle,
  isAutomatic = true,
  addOrientation,
}) => {
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;

  if (!elements || elements.length === 0) return null;

  const items = elements.map((el) => (
    <div key={el.id} className={cellClassName} style={cellStyle}>
      <RenderItem element={el} />
    </div>
  ));

  if (!isEditMode) {
    return (
      <div className={className} style={style}>
        {items}
      </div>
    );
  }

  const inferredOrientation =
    /\bflex\b/.test(className) && !/\bflex-col\b/.test(className) ? "vertical" : "horizontal";
  const interOrientation = addOrientation || inferredOrientation;

  const itemIds = elements.map((e) => makeSortableId(e));
  return (
    <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
      <div className={className} style={style}>
        {elements.map((el, idx) => (
          <div
            key={el.id}
            className={`relative ${cellClassName}`.trim()}
            style={cellStyle}
          >
            {!isMediaElement(el) && (
              <InterItemAddButton
                compId={compId}
                order={el.order ?? idx}
                orientation={interOrientation}
              />
            )}
            <SortableItemWrapper
              id={makeSortableId(el)}
              data={{
                parentType: "component",
                parentId: compId,
                kind: "item",
                itemType: el.type,
                index: idx,
                autoLayout: true,
                autoBucket: null,
              }}
            >
              <RenderItem element={el} />
            </SortableItemWrapper>
          </div>
        ))}
      </div>
    </SortableContext>
  );
};

export const EmptySlotPlaceholder = ({ label = "Add content" }) => (
  <div className="flex items-center justify-center py-8 opacity-50 text-sm">
    {label}
  </div>
);

const BUCKET_LABELS = {
  image: "Image",
  title: "Title",
  text: "Text",
  button_collection: "Button Collection",
  button: "Button",
  file: "File",
};

/**
 * BucketAddButton — renders a horizontal add-button indicator at the end
 * of a bucket section. Clicking opens the FULL `AddElementPanel` so the
 * editor can choose ANY element type to add (there is no image-specific
 * shortcut anymore — an Image is just one of the choices in the picker, and
 * is inserted at `order` like any other element).
 */
export const BucketAddButton = ({ compId, bucket, order, className = "" }) => {
  const editContext = useContext(EditContext);
  const { openAddModal } = useAddElement();
  if (!editContext) return null;

  return (
    <div className={`relative w-full ${className}`}>
      <AddButtonOverlay
        layer={`auto-bucket-after-${bucket}`}
        overlayId={`auto-bucket-after-${compId}-${bucket}`}
        strategy="inline"
        orientation="horizontal"
        variant="component"
      >
        <AddButtonWithIndicator
          small
          onClick={(e) => {
            e.stopPropagation();
            openAddModal({ componentId: compId, order }, e);
          }}
        />
      </AddButtonOverlay>
    </div>
  );
};

/**
 * EmptyBucketPlaceholder — dashed-border "Add <Type>" placeholder shown when a
 * bucket has zero items. Because it is explicitly labelled with the bucket's
 * type (e.g. "Add Text"), clicking it creates THAT element type directly via
 * `restrictToType` — `AddElementPanel` auto-creates simple types without
 * showing the picker. (The generic trailing "+" `BucketAddButton` still opens
 * the full picker so any element type can be chosen.) For non-simple buckets
 * `AddElementPanel` falls back to the picker, so passing `restrictToType` is
 * always safe.
 */
export const EmptyBucketPlaceholder = ({ compId, bucket, order, className = "" }) => {
  const editContext = useContext(EditContext);
  const { openAddModal } = useAddElement();
  const buttonRef = useRef(null);
  const { textColor, remeasure } = useBackdropTextColor(buttonRef);
  if (!editContext) return null;

  const label = BUCKET_LABELS[bucket] || bucket;

  return (
    <div className={`auto-add-reveal w-full ${className}`.trim()}>
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={remeasure}
        data-tone={textColor === "black" ? "black" : undefined}
        onClick={(e) => {
          e.stopPropagation();
          openAddModal({ componentId: compId, order, restrictToType: bucket }, e);
        }}
        className="auto-add-affordance w-full py-4 px-3 rounded-lg border-2 border-dashed
                   flex items-center justify-center gap-2
                   cursor-pointer bg-transparent"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        <span className="auto-add-affordance__label text-xs font-medium">Add {label}</span>
      </button>
    </div>
  );
};

const BREAKPOINT_ORDER = [
  "extra_small",
  "small",
  "medium",
  "large",
  "extra_large",
];

export function getBreakpointLimit(modeDefinition, breakpoint) {
  const limits = modeDefinition?.max_units_per_row || {};
  const direct = Number(limits?.[breakpoint]);
  if (Number.isFinite(direct) && direct > 0) return direct;
  for (let i = BREAKPOINT_ORDER.indexOf(breakpoint); i >= 0; i -= 1) {
    const fallback = Number(limits?.[BREAKPOINT_ORDER[i]]);
    if (Number.isFinite(fallback) && fallback > 0) return fallback;
  }
  return 1;
}

export function chunkElements(elements, size) {
  const chunks = [];
  const chunkSize = Math.max(1, Number(size) || 1);
  for (let i = 0; i < elements.length; i += chunkSize) {
    chunks.push(elements.slice(i, i + chunkSize));
  }
  return chunks;
}
